import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal'
import { useState, useEffect, useContext, useRef } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { ButtonAddGroup, StyledAsyncSelect, StyledModalFooter, StyledModalFooterAddMemberOnGroup, StyledText } from '../styles'
import { Input } from 'reactstrap'
import { LabelInput, LabelNames, StyledCardTitle } from '../../userRegister/style'
import { useToast } from '../../../context/Toast'
import { StyledModal } from '../modalUser/styles'
import debounce from 'debounce-promise'
import { getUsersByCompany, searchUsers } from '../../../services/user/user'
import { useAuth } from '../../../context/AuthContext'
import { Autocomplete, Box, Button, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from '@mui/material'
import PersonRemoveAlt1OutlinedIcon from '@mui/icons-material/PersonRemoveAlt1Outlined';
import './modal-styles.css'
import { getGroupByCompany, patchGroup } from '../../../services/group/group'
import { GroupStatus } from '../../../enums/group'
import { isMemberName } from 'typescript'
import { User } from '../../../interfaces/user.interface'
import AutocompleteComponent from '../Autocomplete'
import { getFormmatedUserFullName } from '../../../services/utils/functions'
import { GroupMemberDataFromSearchResult } from '../../../interfaces/group.interface'
import UserStatusLegend from '../components/UserStatusLegend'
import { getApiErrorMessage } from '../../../services/utils/apiError'
import { getInformedRequesterId } from '../../../services/utils/requester'
interface IFormState {
  name: string,
  users: { id: string, label: string, status?: number | null }[],
  responsible_users?: { id: string, label: string, status?: number | null }[],
  status: GroupStatus,
}

interface ModalEditGroupProps {
  show: boolean;
  group_id: string;
  group_name: string;
  group_status: GroupStatus;
  group_members: GroupMemberDataFromSearchResult[];
  responsible_users: GroupMemberDataFromSearchResult[];
  companyUserList: User[];
  onClose: () => void;
}

const buildMemberOptions = (members: GroupMemberDataFromSearchResult[]) =>
  members.map((member) => ({
    id: member.id,
    label: getFormmatedUserFullName(member, true),
    status: member.status,
  }));

const extractIds = (items: { id: string }[]) => items.map((item) => item.id).filter((id) => !!id);

const areSameIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();

  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const getGroupStatusLabel = (status: GroupStatus) => {
  if (status === GroupStatus.active) {
    return 'groups.statusActive';
  }

  if (status === GroupStatus.inactive) {
    return 'groups.statusInactive';
  }

  if (status === GroupStatus.disabled_by_support) {
    return 'groups.statusDisabled';
  }

  return 'groups.statusUnknown';
};

function ModalEditGroup({ show, group_id, group_name, group_status, group_members, responsible_users, companyUserList, onClose }: ModalEditGroupProps) {
  const { t } = useTranslation('settings');
  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<IFormState>({
    name: group_name,
    users: buildMemberOptions(group_members),
    responsible_users: buildMemberOptions(responsible_users),
    status: group_status
  });
  const [groupNameError, setGroupNameError] = useState<string | null>(null);
  const initialFormSnapshotRef = useRef({
    name: group_name.trim(),
    users: extractIds(group_members),
    responsible_users: extractIds(responsible_users),
    status: group_status,
  });
  const isStatusLocked = group_status === GroupStatus.disabled_by_support;

  useEffect(() => {
    if (show) {
      setFormData({
        name: group_name,
        users: buildMemberOptions(group_members),
        responsible_users: buildMemberOptions(responsible_users),
        status: group_status
      });
      initialFormSnapshotRef.current = {
        name: group_name.trim(),
        users: extractIds(group_members),
        responsible_users: extractIds(responsible_users),
        status: group_status,
      };
      setGroupNameError(null);
      setLoading(false);
    } else {
      setGroupNameError(null);
      setLoading(false);
    }
  }, [show, group_members, group_name, group_status, responsible_users]);


  const handleClose = () => {
    setGroupNameError(null);
    setFormData({
      name: '',
      users: [],
      responsible_users: [],
      status: GroupStatus.active
    });
    onClose();
  };


  const onSubmit = async () => {
    if (isStatusLocked) {
      showToast(t('groups.disabled'), 'warning', 5000);
      return;
    }

    const normalizedName = formData.name.trim();
    if (!normalizedName) {
      setGroupNameError('O nome do grupo é obrigatório.');
      return;
    }
    setGroupNameError(null);

    const informedRequesterId = getInformedRequesterId(user?.user?.id);
    if (!informedRequesterId) {
      showToast(t('groups.requesterMissing'), 'error');
      return;
    }

    const initialSnapshot = initialFormSnapshotRef.current;
    const nextUsers = extractIds(formData.users);
    const nextResponsibleUsers = extractIds(formData.responsible_users ?? []);
    const dataToSend: {
      id: string;
      informedRequesterId: string;
      name?: string;
      users?: string[];
      responsible_users?: string[];
      status?: GroupStatus;
    } = {
      id: group_id,
      informedRequesterId,
    };

    if (normalizedName !== initialSnapshot.name) {
      dataToSend.name = normalizedName;
    }

    if (!areSameIds(nextUsers, initialSnapshot.users)) {
      dataToSend.users = nextUsers;
    }

    if (!areSameIds(nextResponsibleUsers, initialSnapshot.responsible_users)) {
      dataToSend.responsible_users = nextResponsibleUsers;
    }

    if (formData.status !== initialSnapshot.status) {
      dataToSend.status = formData.status;
    }

    if (Object.keys(dataToSend).length === 2) {
      showToast(t('groups.noChanges'), 'info', 4000);
      return;
    }

    try {
      setLoading(true);
      
      const response = await patchGroup(dataToSend);

      setFormData({
        name: '',
        users: [],
        responsible_users: [],
        status: GroupStatus.active
      });

      if (response?.action === 'NO_OP_STATUS_ALREADY_CURRENT') {
        showToast(response?.message || t('groups.statusAlreadyCurrent'), 'info', 5000);
      } else {
        showToast(response?.message || t('groups.updated'), 'success');
      }
      onClose();

    } catch (error: any) {
      console.error(error);
      showToast(
        getApiErrorMessage(error, 'Não foi possível atualizar esse grupo. Tente novamente mais tarde.'),
        'error',
        8000
      );
    } finally {
      setLoading(false);
    }
  };


  // Caso a variável booleana de exibição seja falsa o componente não será renderizado
  if (show == false) {
    return null
  }

  return (
    <>
      <StyledModal show={show} onHide={onClose} centered size='lg' >
        <Modal.Header style={{ background: '#FFF' }}>
          <Modal.Title style={{ color: '#808086' }}>{t('groups.editTitle')} - <span style={{ color: '#A5A5AA' }}>{t('groups.memberCount', { count: group_members?.length || 0 })}</span></Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#FFF' }}>
          <Row>
            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-3" controlId="groupName">
                <TextField
                  id="group_name"
                  variant="outlined"
                  label={String(t('groups.name'))}
                  placeholder={String(t('groups.namePlaceholder'))}
                  value={formData.name}
                  disabled={loading || isStatusLocked}
                  onChange={(event: any) => {
                    const nextValue = event.target.value;

                    setFormData((prev) => ({
                      ...prev,
                      name: nextValue,
                    }));

                    if (nextValue.trim()) setGroupNameError(null);
                  }}
                  error={!!groupNameError}
                  helperText={groupNameError}
                  sx={{
                    width: '100%',
                    borderRadius: '0.625rem',
                    backgroundColor: '#F1F1F1',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.625rem',
                      '& input': {
                        backgroundColor: 'transparent',
                        fontSize: '16px',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c9c9c9',
                    },
                  }}
                />
              </Form.Group>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginTop: '0.9rem', marginBottom: '0rem', flexWrap: 'wrap' }}>
                <UserStatusLegend />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
                  <FormControlLabel
                    sx={{ marginRight: 0 }}
                    control={
                      <Switch
                        checked={formData.status === GroupStatus.active}
                        onChange={() => {
                          setFormData((prev) => ({
                            ...prev,
                            status: prev.status === GroupStatus.active ? GroupStatus.inactive : GroupStatus.active,
                          }));
                        }}
                        disabled={loading || isStatusLocked}
                      />
                    }
                    label={String(t(getGroupStatusLabel(formData.status)))}
                  />
                  {isStatusLocked && (
                    <Box sx={{ color: '#B42318', fontSize: '12px', textAlign: 'right', maxWidth: '290px' }}>
                      Este grupo está desabilitado pelo suporte e não pode ser alterado por esta tela.
                    </Box>
                  )}
                </div>
              </div>
            </Col>

            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-2" controlId="users">
                <AutocompleteComponent
                  id='group-users-autocomplete'
                  disabled={loading || isStatusLocked}
                  showStatusIndicators
                  onChange={(newValue) => {
                    // Atualiza o estado com os itens selecionados
                    setFormData((prev) => ({
                      ...prev,
                      users: newValue.map((user: any) => ({
                        id: user.id,
                        label: user.label,
                        status: user.status,
                      })), // Garante que o formato seja consistente
                    }));
                  }}
                  onRemove={(removedId) => {
                    // Remove o item pelo ID
                    setFormData((prev) => ({
                      ...prev,
                      users: prev.users.filter((user: any) => user.id !== removedId),
                    }));
                  }}
                  label={String(t('groups.members'))}
                  placeholder={String(t('groups.memberPlaceholder'))}
                  options={companyUserList.map(u => ({id: u.id, label: getFormmatedUserFullName(u, true), status: u.status}))}
                  selectedOptions={formData.users}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={12}>
              <Form.Group className="mb-3 mt-2" controlId="responsible_users">
                <AutocompleteComponent
                  id='group-responsible-users-autocomplete'
                  disabled={loading || isStatusLocked}
                  showStatusIndicators
                  onChange={(newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      responsible_users: newValue.map((user: any) => ({
                        id: user.id,
                        label: user.label,
                        status: user.status,
                      })),
                    }));
                  }}
                  onRemove={(removedId) => {
                    setFormData((prev) => ({
                      ...prev,
                      responsible_users: (prev.responsible_users ?? []).filter((user: any) => user.id !== removedId),
                    }));
                  }}
                  label={String(t('groups.responsible'))}
                  placeholder={String(t('groups.responsiblePlaceholder'))}
                  options={companyUserList.map(u => ({id: u.id, label: getFormmatedUserFullName(u, true), status: u.status}))}
                  selectedOptions={formData.responsible_users ?? []}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>


        <StyledModalFooterAddMemberOnGroup style={{ background: '#FFF'}}>
          <span style={{ color: '#A5A5AA', fontSize: '14px', textAlign: 'left' }}>
            {isStatusLocked
              ? t('groups.locked')
              : t('groups.saveHint')}
          </span>
          <ButtonAddGroup>
            <Button
              sx={{
                textTransform: 'capitalize',
                padding: "0.5em 1.2em",
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 500,
                border: '1px solid #a0a0a0',
              }}
              variant='text'
              onClick={handleClose}
              disabled={loading || isStatusLocked}
            >
              {t('groups.cancel')}
            </Button>
            <Button
              sx={{
                textTransform: 'capitalize',
                background: '#2491FF',
                borderRadius: '2em',
                fontSize: '1em',
                fontWeight: 'semibold ',
                padding: "0.5em 2em",
              }}
              variant='contained'
              onClick={() => {
                onSubmit()
              }}
              disabled={loading}
            >
              {loading ? t('groups.saving') : t('groups.save')}
            </Button>
          </ButtonAddGroup>
        </StyledModalFooterAddMemberOnGroup>
      </StyledModal>
    </>
  )
}

export default ModalEditGroup;
